import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { CreditCard, CalendarClock, CheckCircle2, AlertTriangle, RefreshCw, ShieldCheck, Receipt, Clock, XCircle, Download, TrendingUp } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

const PLAN_LABEL = { monthly: "Bulanan", yearly: "Tahunan" };
const PLAN_PRICE = { monthly: "Rp55.000/bulan", yearly: "Rp550.000/tahun" };

const STATUS_META = {
  settlement: { label: "Berhasil", cls: "bg-emerald-500/12 border-emerald-500/30 text-emerald-600", icon: CheckCircle2 },
  paid: { label: "Berhasil", cls: "bg-emerald-500/12 border-emerald-500/30 text-emerald-600", icon: CheckCircle2 },
  success: { label: "Berhasil", cls: "bg-emerald-500/12 border-emerald-500/30 text-emerald-600", icon: CheckCircle2 },
  pending: { label: "Menunggu", cls: "bg-sk-brand/12 border-sk-brand/30 text-sk-brand", icon: Clock },
  failed: { label: "Gagal", cls: "bg-red-500/12 border-red-500/30 text-red-600", icon: XCircle },
  cancelled: { label: "Dibatalkan", cls: "bg-sk-line border-sk-line text-sk-muted", icon: XCircle },
};

function fmtDate(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
}

function fmtRupiah(n) {
  if (n == null) return "-";
  return "Rp" + Number(n).toLocaleString("id-ID");
}

export default function KelolaLangganan() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [downloading, setDownloading] = useState(null);

  const SUCCESS = ["settlement", "paid", "success"];

  const downloadInvoice = async (orderId) => {
    setDownloading(orderId);
    try {
      const res = await api.get(`/transactions/${orderId}/invoice`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${orderId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Gagal mengunduh invoice. Coba lagi ya.");
    } finally {
      setDownloading(null);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/transactions");
        if (alive) setHistory(res.data || []);
      } catch (e) {
        if (alive) setHistory([]);
      } finally {
        if (alive) setLoadingHistory(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const plan = user?.subscription_plan || "monthly";
  const exp = user?.subscription_expires_at ? new Date(user.subscription_expires_at) : null;
  const now = new Date();
  const active = user?.subscription_status === "active" && exp && exp > now;
  const daysLeft = exp ? Math.max(0, Math.ceil((exp - now) / 86400000)) : 0;
  const expiringSoon = active && daysLeft <= 7;

  const perpanjang = () => navigate(`/checkout?plan=${plan}&renew=1`);
  const upgrade = () => navigate(`/checkout?plan=yearly&renew=1`);
  const canUpgrade = active && plan === "monthly";

  return (
    <div className="space-y-8" data-testid="kelola-langganan">
      <div>
        <p className="inline-flex items-center gap-2 rounded-full border border-sk-line bg-sk-surface px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-sk-muted">
          <span className="h-1.5 w-1.5 rounded-full bg-sk-brand" /> Akun
        </p>
        <h1 className="mt-4 font-extrabold text-3xl sm:text-4xl tracking-tight text-sk-cream">Kelola Langganan</h1>
        <p className="text-sk-muted mt-2 text-sm sm:text-base">Pantau status langganan kamu dan perpanjang kapan saja.</p>
      </div>

      {/* Status card */}
      <div className="rounded-2xl border border-sk-line bg-sk-surface overflow-hidden" data-testid="subscription-status-card">
        <div className="p-6 sm:p-8">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <span className="h-12 w-12 rounded-xl bg-sk-brand/12 flex items-center justify-center shrink-0">
                <CreditCard className="h-6 w-6 text-sk-brand" strokeWidth={1.7} />
              </span>
              <div>
                <p className="text-sm text-sk-muted">Paket kamu</p>
                <p className="font-bold text-lg text-sk-cream" data-testid="sub-plan">{PLAN_LABEL[plan] || "—"} · {PLAN_PRICE[plan] || ""}</p>
              </div>
            </div>
            {active ? (
              <span data-testid="sub-status-badge" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/12 border border-emerald-500/30 px-3 py-1.5 text-xs font-bold text-emerald-600">
                <CheckCircle2 className="h-3.5 w-3.5" /> Aktif
              </span>
            ) : (
              <span data-testid="sub-status-badge" className="inline-flex items-center gap-1.5 rounded-full bg-sk-brand/12 border border-sk-brand/30 px-3 py-1.5 text-xs font-bold text-sk-brand">
                <AlertTriangle className="h-3.5 w-3.5" /> Tidak aktif
              </span>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mt-7">
            <div className="rounded-xl border border-sk-line bg-sk-bg p-4">
              <p className="flex items-center gap-2 text-xs font-semibold text-sk-muted"><CalendarClock className="h-4 w-4 text-sk-brand" /> Berlaku sampai</p>
              <p className="font-bold text-sk-cream mt-1.5" data-testid="sub-expiry">{fmtDate(user?.subscription_expires_at)}</p>
            </div>
            <div className="rounded-xl border border-sk-line bg-sk-bg p-4">
              <p className="flex items-center gap-2 text-xs font-semibold text-sk-muted"><ShieldCheck className="h-4 w-4 text-sk-brand" /> Sisa waktu</p>
              <p className="font-bold text-sk-cream mt-1.5" data-testid="sub-days-left">{active ? `${daysLeft} hari lagi` : "Sudah berakhir"}</p>
            </div>
          </div>

          {expiringSoon && (
            <p className="mt-4 flex items-center gap-2 rounded-xl bg-sk-brand/8 border border-sk-brand/25 px-4 py-3 text-sm text-sk-cream" data-testid="expiring-soon-note">
              <AlertTriangle className="h-4 w-4 text-sk-brand shrink-0" /> Langganan kamu akan berakhir dalam {daysLeft} hari. Perpanjang biar aksesnya nggak putus.
            </p>
          )}

          {canUpgrade && (
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-sk-brand/30 bg-sk-brand/8 px-4 py-3.5" data-testid="upgrade-banner">
              <div className="flex items-start gap-2.5">
                <TrendingUp className="h-5 w-5 text-sk-brand shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-sk-cream text-sm">Upgrade ke Tahunan, hemat 2 bulan</p>
                  <p className="text-xs text-sk-muted mt-0.5">Rp550.000/tahun (≈ Rp45.800/bulan) — bebas pusing perpanjang tiap bulan.</p>
                </div>
              </div>
              <button onClick={upgrade} data-testid="upgrade-yearly-btn" className="shrink-0 inline-flex items-center justify-center gap-2 rounded-full bg-sk-brand text-sk-ink font-bold text-sm px-5 py-2.5 hover:bg-sk-brandDark transition-colors">
                <TrendingUp className="h-4 w-4" /> Upgrade ke Tahunan
              </button>
            </div>
          )}
        </div>

        <div className="border-t border-sk-line px-6 sm:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 bg-sk-surface">
          <p className="text-xs text-sk-muted flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Pembayaran diproses aman lewat Midtrans</p>
          <button onClick={perpanjang} data-testid="renew-subscription-btn" className="w-full sm:w-auto flex items-center justify-center gap-2 rounded-full bg-sk-brand text-sk-ink font-bold px-7 py-3 hover:bg-sk-brandDark transition-colors shadow-lg shadow-sk-brand/20">
            <RefreshCw className="h-4 w-4" /> Perpanjang Langganan
          </button>
        </div>
      </div>

      <p className="text-xs text-sk-muted leading-relaxed max-w-xl">
        Langganan tidak diperpanjang otomatis. Kamu bisa memperpanjang kapan saja — sisa masa aktif yang ada akan tetap dihitung. Setelah perpanjangan berhasil, tanggal berlaku akan diperbarui.
      </p>

      {/* Riwayat Transaksi */}
      <div>
        <h2 className="flex items-center gap-2 font-bold text-lg text-sk-cream"><Receipt className="h-5 w-5 text-sk-brand" /> Riwayat Transaksi</h2>
        <p className="text-sm text-sk-muted mt-1">Daftar pembayaran kamu sebelumnya.</p>

        <div className="mt-4 rounded-2xl border border-sk-line bg-sk-surface overflow-hidden" data-testid="transaction-history">
          {loadingHistory ? (
            <div className="p-8 flex items-center justify-center text-sk-muted text-sm gap-2"><RefreshCw className="h-4 w-4 animate-spin" /> Memuat riwayat...</div>
          ) : history.length === 0 ? (
            <div className="p-8 text-center" data-testid="history-empty">
              <span className="h-12 w-12 rounded-xl bg-sk-bg flex items-center justify-center mx-auto mb-3"><Receipt className="h-6 w-6 text-sk-muted" /></span>
              <p className="text-sk-cream font-semibold">Belum ada transaksi</p>
              <p className="text-sk-muted text-sm mt-1">Pembayaran kamu akan muncul di sini.</p>
            </div>
          ) : (
            <ul className="divide-y divide-sk-line">
              {history.map((t) => {
                const meta = STATUS_META[t.status] || STATUS_META.pending;
                const Icon = meta.icon;
                return (
                  <li key={t.orderId} data-testid={`history-row-${t.orderId}`} className="flex items-center justify-between gap-4 px-5 py-4">
                    <div className="min-w-0">
                      <p className="font-semibold text-sk-cream text-sm">{PLAN_LABEL[t.plan] || "Langganan"} · {t.paymentMethod || "QRIS"}</p>
                      <p className="text-xs text-sk-muted mt-0.5">{fmtDate(t.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="font-bold text-sk-cream text-sm tabular-nums">{fmtRupiah(t.amount)}</span>
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-bold ${meta.cls}`}>
                        <Icon className="h-3 w-3" /> {meta.label}
                      </span>
                      {SUCCESS.includes(t.status) && (
                        <button
                          onClick={() => downloadInvoice(t.orderId)}
                          disabled={downloading === t.orderId}
                          data-testid={`invoice-btn-${t.orderId}`}
                          title="Unduh invoice (PDF)"
                          className="inline-flex items-center gap-1.5 rounded-full border border-sk-line px-3 py-1.5 text-[11px] font-semibold text-sk-cream hover:border-sk-brand hover:text-sk-brand transition-colors disabled:opacity-60"
                        >
                          {downloading === t.orderId ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                          <span className="hidden sm:inline">Invoice</span>
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
